import { useState, useEffect, useRef } from 'react'
import useDebouncedWrite from '../../hooks/useDebouncedWrite'
import CellRender from './CellRender'
import { runCell } from '../../helpers/intelligence/intelligenceWrites'
import { DRIFT_LABELS } from '../../helpers/intelligence/driftLabels'
import { SECTIONS, sectionsOf, sameSections } from '../../helpers/intelligence/insightSections'
import InsightPracticeFields from './InsightPracticeFields'
import { practiceRowsOf, leadTimeOf, samePractice } from '../../helpers/intelligence/insightPractice'

const STATUS_LABELS = { draft: 'concept', published: 'gepubliceerd', rejected: 'afgewezen' }


/**
 * Eén inzicht onder een onderzoeksvraag.
 *
 * Welke cijfers je ziet hangt af van de status, en dat is opzet:
 * - gepubliceerd → de BEVROREN momentopname (dat is wat de buitenwereld ziet)
 * - concept/afgewezen → een verse berekening
 * Beide door elkaar tonen zou de redacteur laten twijfelen welke getallen nu
 * publiek staan.
 *
 * AI-CONTEXT: See docs/features/intelligence-research-questions.md (§6)
 *
 * @param {object} props
 * @param {object} props.insight - het QuestionInsights-document
 * @param {(payload: {sections: import('../../helpers/intelligence/insightSections').InsightSections, title: string, practice: Array<import('../../helpers/intelligence/insightPractice').PracticeRow>, leadTime: string}) => Promise<void>} props.onSaveText - concept opslaan met de titel, de drie delen en de praktijkkant
 * @param {(payload: {sections: import('../../helpers/intelligence/insightSections').InsightSections, title: string, practice: Array<import('../../helpers/intelligence/insightPractice').PracticeRow>, leadTime: string}) => Promise<void>} props.onPublish - publiceren met de titel, de drie delen en de praktijkkant
 * @param {() => Promise<void>} props.onUnpublish - depubliceren
 * @param {() => Promise<void>} props.onReject - afwijzen (blijft bewaard)
 * @param {() => Promise<void>} props.onDelete - zacht verwijderen
 * @param {boolean} [props.showOutput] - Toon de uitkomst van de cel boven het tekstvak; uit op het celdetail, waar dezelfde cijfers al in de kop en de onderbouwing staan
 * @param {string} [props.publishBlockedReason] - Waarom er niet gepubliceerd kan worden; leeg = het kan wel
 */
/**
 * Het label dat de publieke API als titel zou gebruiken zonder geredigeerde
 * titel: het cellabel uit de bevroren momentopname. Staat als placeholder in
 * het titelveld, zodat zichtbaar is wát er anders naar buiten gaat.
 *
 * @param {{Published?: {evidence?: {cell?: {label?: string}}}}} insight
 * @returns {string}
 */
const afgeleideTitel = (insight) =>
    (insight && insight.Published && insight.Published.evidence
        && insight.Published.evidence.cell && insight.Published.evidence.cell.label) || 'Nog geen titel'

const InsightCard = ({
    insight, onSaveText, onPublish, onUnpublish, onReject, onDelete, showOutput = true,
    publishBlockedReason = '',
}) => {
    const [sections, setSections] = useState(sectionsOf(insight))
    const [titel, setTitel] = useState(insight.Title || '')
    // De praktijkkant: regels uit gemodereerde praktijkbijdragen plus de
    // doorlooptijd. Staat naast de tekst omdat het ook geredigeerde tekst is —
    // geen berekening — en dus niet in de bevroren momentopname hoort.
    const [practice, setPractice] = useState(practiceRowsOf(insight))
    const [leadTime, setLeadTime] = useState(leadTimeOf(insight))
    const [live, setLive] = useState(/** @type {object|null} */(null))
    const [liveError, setLiveError] = useState('')
    const [busy, setBusy] = useState(false)
    const [actionError, setActionError] = useState('')
    const isPublished = insight.Status === 'published'

    // Het tekstvak volgt de opgeslagen tekst alleen zolang de redacteur er
    // niets in veranderd heeft. Dat is nodig sinds Decca de tekst vanuit de
    // chat opslaat (save_insight_text): zonder dit zou de opzet pas na een
    // herlaad verschijnen. Maar een snapshot mag nooit overschrijven wat er
    // getypt staat — 'Opslaan' zou dan iets anders wegschrijven dan het
    // scherm toont. Daarom: bij een ander inzicht altijd; bij dezelfde alleen
    // als het vak nog gelijk is aan de vorige opgeslagen tekst.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    /** @type {React.MutableRefObject<{id: string, sections: import('../../helpers/intelligence/insightSections').InsightSections}>} */
    const opgeslagen = useRef({
        id: insight.ID,
        sections: sectionsOf(insight),
        titel: insight.Title || '',
        practice: practiceRowsOf(insight),
        leadTime: leadTimeOf(insight),
    })
    const bewaardeTekst = JSON.stringify(insight.Sections || null) + '|' + (insight.Text || '') + '|' + (insight.Title || '')
        + '|' + JSON.stringify(insight.Practice || null) + '|' + (insight.LeadTime || '')
    useEffect(() => {
        const nieuw = sectionsOf(insight)
        const nieuweTitel = insight.Title || ''
        const nieuwePractice = practiceRowsOf(insight)
        const nieuweLeadTime = leadTimeOf(insight)
        const anderInzicht = opgeslagen.current.id !== insight.ID
        // De vórige stand eerst vastpakken. De updaters hieronder draaien pas
        // als React de queue verwerkt, en dan is `opgeslagen.current` al de
        // nieuwe stand — de vergelijking zou zichzelf toetsen en altijd
        // 'veranderd' zeggen, waardoor een binnengekomen tekst nooit meer
        // verschijnt. (React rekent alleen de eerste updater van een reeks
        // meteen uit; daardoor werkte het voor het eerste vak wél en voor de
        // rest niet.)
        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
        const vorige = opgeslagen.current
        setSections((huidig) => (anderInzicht || sameSections(huidig, vorige.sections) ? nieuw : huidig))
        // Zelfde regel voor de titel: een snapshot mag nooit overschrijven wat
        // er getypt staat, anders slaat 'Opslaan' iets anders op dan je ziet.
        setTitel((huidig) => (anderInzicht || huidig === vorige.titel ? nieuweTitel : huidig))
        // En voor de praktijkregels: een halfgetypte randvoorwaarde mag niet
        // verdwijnen omdat er elders iets aan het inzicht veranderde.
        setPractice((huidig) => (anderInzicht || samePractice(huidig, vorige.practice) ? nieuwePractice : huidig))
        setLeadTime((huidig) => (anderInzicht || huidig === vorige.leadTime ? nieuweLeadTime : huidig))
        opgeslagen.current = {
            id: insight.ID, sections: nieuw, titel: nieuweTitel, practice: nieuwePractice, leadTime: nieuweLeadTime,
        }
        // `insight` zelf is bij elke snapshot een nieuw object; de inhoud die
        // telt staat in de sleutel hierboven.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insight.ID, bewaardeTekst])

    // Een concept heeft geen bevroren uitkomst, dus die wordt live gehaald.
    useEffect(() => {
        // Zonder weergave ook geen berekening: die zou alleen een verborgen tabel voeden.
        if (!showOutput || isPublished || !insight.CellType) return undefined
        let cancelled = false
        setLiveError('')
        setLive(null)
        runCell({ cellType: insight.CellType, params: insight.Params || {} })
            .then((result) => { if (!cancelled) setLive(result.output) })
            // Fouten zichtbaar maken: een lege kaart leest als "geen resultaten".
            .catch((err) => { if (!cancelled) setLiveError(err.message || 'Uitrekenen mislukt') })
        return () => { cancelled = true }
        // `insight.Params` bewust weggelaten: dat is bij elke snapshot een
        // nieuw object, en op die identiteit afgaan zou de cel eindeloos
        // opnieuw laten uitrekenen. De parameters veranderen alleen als het
        // inzicht zelf verandert.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insight.ID, insight.CellType, isPublished, showOutput])

    // Een concept slaat vanzelf op, zoals vrijwel elk scherm in de app (de user
    // verwachtte dat, 2026-09-30). Niet per toetsaanslag maar na een korte
    // pauze, en alsnog bij het verlaten van de pagina. Een PUBLICATIE niet:
    // opslaan is daar opnieuw publiceren, en dat hoort een handeling te zijn.
    // AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail
    const autosave = useDebouncedWrite((_key, payload) => onSaveText(payload))
    const { save: planAutosave } = autosave
    useEffect(() => {
        if (isPublished) return
        // Alleen wat de redacteur zelf veranderde. Een snapshot zet de state
        // óók (het vak volgt de opgeslagen tekst) en werkt de ref bij; zou dat
        // hier meetellen, dan ontstaat een lus van opslaan en terugkrijgen.
        const o = opgeslagen.current
        const ongewijzigd = sameSections(sections, o.sections) && titel === o.titel
            && samePractice(practice, o.practice) && leadTime === o.leadTime
        if (ongewijzigd) return
        planAutosave('tekst', { sections, title: titel, practice, leadTime })
    }, [sections, titel, practice, leadTime, isPublished, planAutosave])

    const run = async (fn) => {
        setBusy(true)
        setActionError('')
        try {
            await fn()
        } catch (err) {
            setActionError(err.message || 'Actie mislukt')
        } finally {
            setBusy(false)
        }
    }

    const drift = insight.Drift

    const output = isPublished ? insight.Published : live

    return (
        <div className="intelligence-panel">
            {/* Geen celtype-sleutel op de kaart: dat is registry-vocabulaire, geen
                redactie-informatie. Op het celdetail (showOutput uit) ook geen
                statusbadge — de status staat daar in de knoppen (Publiceren /
                Depubliceren). 'Herbeoordelen' blijft altijd staan. */}
            {(showOutput || (drift && drift.needsReview)) && (
                <div className="intelligence-question-card__meta">
                    {showOutput && (
                        <span className={`intelligence-badge intelligence-badge--${insight.Status || 'draft'}`}>
                            {STATUS_LABELS[insight.Status] || insight.Status}
                        </span>
                    )}
                    {drift && drift.needsReview && (
                        <span className="intelligence-badge intelligence-badge--review">herbeoordelen</span>
                    )}
                </div>
            )}

            {drift && drift.needsReview && (
                <p className="intelligence-panel__hint">
                    Sinds publicatie: {drift.reasons.map((r) => DRIFT_LABELS[r.kind] || r.kind).join(', ')}.
                </p>
            )}

            {showOutput && isPublished && (
                <p className="intelligence-panel__hint">
                    Dit zijn de bevroren cijfers van het moment van publiceren — precies wat de
                    marketingsite serveert.
                </p>
            )}

            {showOutput && (liveError
                ? <p className="intelligence-state intelligence-state--error">{liveError}</p>
                : (!isPublished && !live && insight.CellType)
                    ? <p className="intelligence-state">Uitkomst berekenen…</p>
                    : <CellRender render={insight.Render} output={output} />)}

            {/* Het inzicht schrijf je hier, in drie vaste delen: wat, waarom, hoe
                (in de praktijk). Een opzet vraag je aan Decca in de chat (knop op
                de pagina); na akkoord zet Decca de drie delen hier neer. */}
            {/* De titel: wat de lezer als kop ziet, en wat de publieke API
                meestuurt. Zonder deze valt de API terug op het afgeleide
                cellabel, en dat is machinetaal. */}
            <div className="intelligence-insight-section">
                <label htmlFor={`insight-titel-${insight.ID}`}>Titel</label>
                <input
                    id={`insight-titel-${insight.ID}`}
                    className="intelligence-insight-title"
                    type="text"
                    value={titel}
                    placeholder={afgeleideTitel(insight)}
                    onChange={(e) => {
                        // Eerst de waarde uitlezen, dan pas de state zetten.
                        // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                        const waarde = e.target.value
                        setTitel(waarde)
                    }}
                />
            </div>

            {SECTIONS.map((deel) => (
                <div key={deel.key} className="intelligence-insight-section">
                    <label htmlFor={`insight-${deel.key}-${insight.ID}`}>{deel.label}</label>
                    <textarea
                        id={`insight-${deel.key}-${insight.ID}`}
                        className="intelligence-insight-text"
                        rows={5}
                        value={sections[deel.key]}
                        onChange={(e) => {
                            // Eerst de waarde uitlezen, dan pas de updater.
                            // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                            const waarde = e.target.value
                            setSections((huidig) => ({ ...huidig, [deel.key]: waarde }))
                        }}
                        placeholder={deel.placeholder}
                    />
                </div>
            ))}

            {/* De stand van het automatisch opslaan, bij de vakken en blijvend
                zichtbaar; 'Opgeslagen' verdwijnt pas als er weer iets te
                bewaren valt. */}
            {!isPublished && autosave.status !== 'idle' && (
                <p className={`intelligence-autosave${autosave.status === 'error' ? ' intelligence-autosave--error' : ''}`}>
                    {autosave.status === 'saving' && 'Opslaan…'}
                    {autosave.status === 'saved' && 'Opgeslagen'}
                    {autosave.status === 'error' && `Niet opgeslagen: ${autosave.error}`}
                </p>
            )}

            <InsightPracticeFields
                insightId={insight.ID}
                rows={practice}
                leadTime={leadTime}
                onRowsChange={setPractice}
                onLeadTimeChange={setLeadTime}
            />

            {actionError && <p className="intelligence-state--error">{actionError}</p>}

            {/* De poort op het effect (ADR-040). De échte weigering komt van de
                server; deze regel zorgt dat de knop niet iets aanbiedt dat
                gegarandeerd stukloopt, en zegt waarom. Alleen publiceren wordt
                geblokkeerd: eraan werken, afwijzen en depubliceren blijven.
                AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js */}
            {!isPublished && publishBlockedReason && (
                <p className="intelligence-state">{publishBlockedReason}</p>
            )}

            <div className="intelligence-toolbar">
                {/* Op een GEPUBLICEERD inzicht slaat deze knop op via publiceren,
                    niet via het concept. Concept opslaan zet de status terug op
                    'draft', en dan verdwijnt het inzicht stil van de publieke
                    API — je wilde alleen een titel bijschaven en haalt het van
                    de site af. De knop zegt daarom wat hij doet. */}
                <button disabled={busy} onClick={() => run(() => (isPublished ? onPublish : onSaveText)({ sections, title: titel, practice, leadTime }))}>
                    {isPublished ? 'Opslaan en opnieuw publiceren' : 'Opslaan'}
                </button>
                {isPublished
                    ? <button disabled={busy} onClick={() => run(onUnpublish)}>Depubliceren</button>
                    : (
                        <button
                            disabled={busy || !!publishBlockedReason}
                            onClick={() => run(async () => {
                                // Eerst wat nog wachtte: een late conceptopslag ná de
                                // publicatie zou hem stil terugzetten naar concept.
                                await autosave.flush()
                                await onPublish({ sections, title: titel, practice, leadTime })
                            })}
                        >
                            Publiceren
                        </button>
                    )}
                {!isPublished && (
                    <button disabled={busy} onClick={() => run(onReject)}>Afwijzen</button>
                )}
                <div className="intelligence-spacer" />
                <button disabled={busy} onClick={() => run(onDelete)}>Verwijderen</button>
            </div>
        </div>
    )
}

export default InsightCard
